import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  Languages, Globe, Check, RefreshCw, Loader2, Search,
  Plus, Save, Flag
} from 'lucide-react';

// Default translations template
const defaultTranslations = {
  // Navigation
  'nav.home': { en: 'Home', es: 'Inicio' },
  'nav.tours': { en: 'Tours', es: 'Tours' },
  'nav.about': { en: 'About', es: 'Nosotros' },
  'nav.contact': { en: 'Contact', es: 'Contacto' },
  
  // Common
  'common.book_now': { en: 'Book Now', es: 'Reservar Ahora' },
  'common.learn_more': { en: 'Learn More', es: 'Más Información' },
  'common.search': { en: 'Search', es: 'Buscar' },
  'common.loading': { en: 'Loading...', es: 'Cargando...' },
  'common.save': { en: 'Save', es: 'Guardar' },
  'common.cancel': { en: 'Cancel', es: 'Cancelar' },
  'common.delete': { en: 'Delete', es: 'Eliminar' },
  'common.edit': { en: 'Edit', es: 'Editar' },
  'common.view': { en: 'View', es: 'Ver' },
  'common.close': { en: 'Close', es: 'Cerrar' },
  
  // Tours
  'tours.title': { en: 'Explore Our Tours', es: 'Explora Nuestros Tours' },
  'tours.filter': { en: 'Filter Tours', es: 'Filtrar Tours' },
  'tours.duration': { en: 'Duration', es: 'Duración' },
  'tours.price': { en: 'Price', es: 'Precio' },
  'tours.from': { en: 'From', es: 'Desde' },
  'tours.per_person': { en: 'per person', es: 'por persona' },
  'tours.availability': { en: 'Availability', es: 'Disponibilidad' },
  'tours.included': { en: "What's Included", es: 'Qué Incluye' },
  'tours.highlights': { en: 'Highlights', es: 'Destacados' },
  
  // Booking
  'booking.title': { en: 'Book Your Tour', es: 'Reserva Tu Tour' },
  'booking.select_date': { en: 'Select Date', es: 'Seleccionar Fecha' },
  'booking.select_time': { en: 'Select Time', es: 'Seleccionar Hora' },
  'booking.guests': { en: 'Number of Guests', es: 'Número de Personas' },
  'booking.contact_info': { en: 'Contact Information', es: 'Información de Contacto' },
  'booking.name': { en: 'Full Name', es: 'Nombre Completo' },
  'booking.email': { en: 'Email', es: 'Correo Electrónico' },
  'booking.phone': { en: 'Phone', es: 'Teléfono' },
  'booking.special_requests': { en: 'Special Requests', es: 'Solicitudes Especiales' },
  'booking.total': { en: 'Total', es: 'Total' },
  'booking.confirm': { en: 'Confirm Booking', es: 'Confirmar Reserva' },
  'booking.success': { en: 'Booking Confirmed!', es: '¡Reserva Confirmada!' },
  
  // Footer
  'footer.about': { en: 'About Us', es: 'Sobre Nosotros' },
  'footer.contact': { en: 'Contact Us', es: 'Contáctanos' },
  'footer.faq': { en: 'FAQ', es: 'Preguntas Frecuentes' },
  'footer.terms': { en: 'Terms of Service', es: 'Términos de Servicio' },
  'footer.privacy': { en: 'Privacy Policy', es: 'Política de Privacidad' },
  'footer.cancellation': { en: 'Cancellation Policy', es: 'Política de Cancelación' },
  
  // Hero
  'hero.title': { en: 'Discover Puerto Rico', es: 'Descubre Puerto Rico' },
  'hero.subtitle': { en: 'Book unforgettable adventures with local experts', es: 'Reserva aventuras inolvidables con expertos locales' },
  'hero.cta': { en: 'Explore Tours', es: 'Explorar Tours' },
};

export default function LanguageSettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState('en');
  const [translations, setTranslations] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [enabledLanguages, setEnabledLanguages] = useState({ en: true, es: true });

  useEffect(() => {
    loadTranslations();
  }, []);

  const loadTranslations = async () => {
    setLoading(true);
    try {
      // Load both languages
      const [enRes, esRes] = await Promise.all([
        api.get('/translations/en'),
        api.get('/translations/es')
      ]);
      
      // Merge with defaults
      const merged = { ...defaultTranslations };
      
      // Apply saved translations
      if (enRes.data?.translations) {
        Object.entries(enRes.data.translations).forEach(([key, value]) => {
          if (merged[key]) {
            merged[key].en = value;
          } else {
            merged[key] = { en: value, es: '' };
          }
        });
      }
      
      if (esRes.data?.translations) {
        Object.entries(esRes.data.translations).forEach(([key, value]) => {
          if (merged[key]) {
            merged[key].es = value;
          } else {
            merged[key] = { en: '', es: value };
          }
        });
      }
      
      setTranslations(merged);
    } catch (error) {
      console.error('Error loading translations:', error);
      setTranslations(defaultTranslations);
    } finally {
      setLoading(false);
    }
  };

  const saveTranslations = async () => {
    setSaving(true);
    try {
      // Save English translations
      const enTranslations = {};
      Object.entries(translations).forEach(([key, value]) => {
        enTranslations[key] = value.en;
      });
      
      // Save Spanish translations
      const esTranslations = {};
      Object.entries(translations).forEach(([key, value]) => {
        esTranslations[key] = value.es;
      });
      
      await Promise.all([
        api.post('/admin/translations/en', enTranslations),
        api.post('/admin/translations/es', esTranslations)
      ]);
      
      toast.success('Translations saved successfully!');
    } catch (error) {
      console.error('Error saving translations:', error);
      toast.error('Failed to save translations');
    } finally {
      setSaving(false);
    }
  };

  const updateTranslation = (key, lang, value) => {
    setTranslations(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [lang]: value
      }
    }));
  };

  const filteredTranslations = Object.entries(translations).filter(([key, value]) =>
    key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    value.en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    value.es?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group translations by category
  const groupedTranslations = filteredTranslations.reduce((acc, [key, value]) => {
    const category = key.split('.')[0];
    if (!acc[category]) acc[category] = [];
    acc[category].push([key, value]);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="language-settings-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Languages className="h-6 w-6 text-blue-500" />
            Multi-Language Support
          </h2>
          <p className="text-muted-foreground">Manage translations for your site</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadTranslations} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={saveTranslations} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save All
          </Button>
        </div>
      </div>

      {/* Language Toggles */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Enabled Languages
        </h3>
        <div className="flex gap-6">
          <div className="flex items-center gap-3 p-4 border rounded-lg">
            <div className="text-3xl">🇺🇸</div>
            <div>
              <p className="font-medium">English</p>
              <p className="text-sm text-muted-foreground">Default language</p>
            </div>
            <Badge variant="default">Active</Badge>
          </div>
          <div className="flex items-center gap-3 p-4 border rounded-lg">
            <div className="text-3xl">🇵🇷</div>
            <div>
              <p className="font-medium">Español</p>
              <p className="text-sm text-muted-foreground">Spanish</p>
            </div>
            <Switch
              checked={enabledLanguages.es}
              onCheckedChange={(checked) => setEnabledLanguages({ ...enabledLanguages, es: checked })}
            />
          </div>
        </div>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search translations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Translations Editor */}
      <Tabs value={activeLanguage} onValueChange={setActiveLanguage}>
        <TabsList>
          <TabsTrigger value="en" className="gap-2">
            <span>🇺🇸</span> English
          </TabsTrigger>
          <TabsTrigger value="es" className="gap-2">
            <span>🇵🇷</span> Español
          </TabsTrigger>
          <TabsTrigger value="side-by-side" className="gap-2">
            Side by Side
          </TabsTrigger>
        </TabsList>

        <TabsContent value="en" className="mt-4">
          {Object.entries(groupedTranslations).map(([category, items]) => (
            <Card key={category} className="p-4 mb-4">
              <h4 className="font-semibold capitalize mb-3 text-blue-600">{category}</h4>
              <div className="space-y-3">
                {items.map(([key, value]) => (
                  <div key={key} className="flex items-center gap-4">
                    <Label className="w-48 text-sm text-muted-foreground truncate">{key}</Label>
                    <Input
                      value={value.en || ''}
                      onChange={(e) => updateTranslation(key, 'en', e.target.value)}
                      placeholder="English translation"
                      className="flex-1"
                    />
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="es" className="mt-4">
          {Object.entries(groupedTranslations).map(([category, items]) => (
            <Card key={category} className="p-4 mb-4">
              <h4 className="font-semibold capitalize mb-3 text-green-600">{category}</h4>
              <div className="space-y-3">
                {items.map(([key, value]) => (
                  <div key={key} className="flex items-center gap-4">
                    <Label className="w-48 text-sm text-muted-foreground truncate">{key}</Label>
                    <Input
                      value={value.es || ''}
                      onChange={(e) => updateTranslation(key, 'es', e.target.value)}
                      placeholder="Traducción en español"
                      className="flex-1"
                    />
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="side-by-side" className="mt-4">
          {Object.entries(groupedTranslations).map(([category, items]) => (
            <Card key={category} className="p-4 mb-4">
              <h4 className="font-semibold capitalize mb-3">{category}</h4>
              <div className="space-y-3">
                {items.map(([key, value]) => (
                  <div key={key} className="grid grid-cols-5 gap-4 items-center">
                    <Label className="text-sm text-muted-foreground truncate">{key}</Label>
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">🇺🇸</span>
                        <Input
                          value={value.en || ''}
                          onChange={(e) => updateTranslation(key, 'en', e.target.value)}
                          placeholder="English"
                        />
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">🇵🇷</span>
                        <Input
                          value={value.es || ''}
                          onChange={(e) => updateTranslation(key, 'es', e.target.value)}
                          placeholder="Español"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Stats */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-blue-700">
              <strong>{Object.keys(translations).length}</strong> translation keys configured
            </p>
            <p className="text-xs text-blue-600">
              English: {Object.values(translations).filter(t => t.en).length} | 
              Spanish: {Object.values(translations).filter(t => t.es).length}
            </p>
          </div>
          <Button onClick={saveTranslations} disabled={saving} size="sm" className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Save Changes
          </Button>
        </div>
      </Card>
    </div>
  );
}
